import { API_URL } from "./api.ts";

export type PublicPlan = {
  code: string;
  name: string;
  description: string | null;
  sequence: number;
  popular: boolean;
  cta: string | null;
  currency: string;
  period: string;
  price_monthly: number;
  included_ecf: number;
  included_users: number;
  extra_user_price: number;
  overage_block_size: number;
  overage_block_price: number;
  extra_rnc_price: number;
  implementation_fee: number;
  implementation_note: string | null;
  features: string[];
};

export const FALLBACK_PLANS: PublicPlan[] = [
  {
    code: "starter",
    name: "Esencial",
    description: "Para el negocio que ya factura pero lleva el inventario en Excel.",
    sequence: 10,
    popular: false,
    cta: "Comenzar Esencial",
    currency: "USD",
    period: "/mes",
    price_monthly: 75,
    included_ecf: 500,
    included_users: 2,
    extra_user_price: 10,
    overage_block_size: 100,
    overage_block_price: 10,
    extra_rnc_price: 0,
    implementation_fee: 350,
    implementation_note: "25% desc. con pago anual",
    features: [
      "Hasta 500 emisiones mensuales",
      "2 usuarios incluidos (adicional: $10/mes)",
      "Odoo ERP preconfigurado (Facturación, Ventas, POS, Inventario y Compras)",
      "Firma con tu P12, envío y validación ante la DGII",
      "RFCE para consumo electrónico (E32)",
      "Notas de crédito y débito ilimitadas",
      "Reportes DGII 606, 607 y 608 automáticos",
      "Base de datos dedicada y respaldo diario",
      "Soporte técnico y fiscal + office hours grupales",
      "Implementación: USD$ 350 (25% desc. con pago anual)",
    ],
  },
  {
    code: "growth",
    name: "Crecimiento",
    description: "Para el negocio con más de un punto de venta o más de una sucursal.",
    sequence: 20,
    popular: true,
    cta: "Comenzar Crecimiento",
    currency: "USD",
    period: "/mes",
    price_monthly: 130,
    included_ecf: 1500,
    included_users: 3,
    extra_user_price: 10,
    overage_block_size: 100,
    overage_block_price: 10,
    extra_rnc_price: 0,
    implementation_fee: 350,
    implementation_note: "GRATIS con pago anual",
    features: [
      "Hasta 1,500 e-CF mensuales",
      "3 usuarios incluidos (adicional: $10/mes)",
      "Todo lo de Esencial + multi-sucursal y multi-caja",
      "Control de turnos y arqueos de caja",
      "Monitoreo activo de rechazos DGII con alertas",
      "Reportes 606, 607, 608 + conciliación fiscal",
      "Base de datos dedicada y respaldo diario",
      "Soporte técnico y fiscal + office hours grupales",
      "Implementación: USD$ 350 (GRATIS con pago anual)",
    ],
  },
  {
    code: "pro",
    name: "Avanzado",
    description: "Para la empresa con operación de almacén real o varias razones sociales.",
    sequence: 30,
    popular: false,
    cta: "Comenzar Avanzado",
    currency: "USD",
    period: "/mes",
    price_monthly: 200,
    included_ecf: 2500,
    included_users: 6,
    extra_user_price: 10,
    overage_block_size: 100,
    overage_block_price: 10,
    extra_rnc_price: 45,
    implementation_fee: 350,
    implementation_note: "GRATIS con pago anual",
    features: [
      "Hasta 2,500 e-CF mensuales",
      "6 usuarios incluidos (adicional: $10/mes)",
      "Todo lo de Crecimiento + múltiples almacenes",
      "Reglas de reabastecimiento e inventario",
      "Multi-compañía y acceso a la API",
      "Emisión en contingencia asistida",
      "Reportes contables e inventarios automatizados",
      "Soporte prioritario (respuesta en 4h hábiles)",
      "Implementación: USD$ 350 (GRATIS con pago anual)",
    ],
  },
];

export function quoteVolume(plans: PublicPlan[], volume: number) {
  const sorted = [...plans].sort((a, b) => a.included_ecf - b.included_ecf);
  if (!sorted.length) {
    return { selected: null as PublicPlan | null, extraCost: 0, extraBlocks: 0, total: 0 };
  }
  let selected = sorted[0];
  for (const plan of sorted) {
    selected = plan;
    if (volume <= plan.included_ecf) break;
  }
  let extraBlocks = 0;
  let extraCost = 0;
  if (volume > selected.included_ecf) {
    extraBlocks = Math.ceil((volume - selected.included_ecf) / selected.overage_block_size);
    extraCost = extraBlocks * selected.overage_block_price;
  }
  return { selected, extraCost, extraBlocks, total: selected.price_monthly + extraCost };
}

export const PYME_PLAN_CODES = ["starter", "growth", "pro"] as const;

export function filterPymePlans(plans: PublicPlan[]): PublicPlan[] {
  const allowed = new Set<string>(PYME_PLAN_CODES);
  return plans.filter((plan) => allowed.has(plan.code));
}

function applyMarketingLabels(plans: PublicPlan[]): PublicPlan[] {
  return plans.map((plan) => {
    const fallback = FALLBACK_PLANS.find((item) => item.code === plan.code);
    if (!fallback) return plan;
    return {
      ...fallback,
      ...plan,
      name: plan.name || fallback.name,
      cta: plan.cta ?? fallback.cta,
      description: plan.description || fallback.description,
      features: (plan.features && plan.features.length > 0) ? plan.features : fallback.features,
    };
  });
}

export async function loadPublicPlans(): Promise<PublicPlan[]> {
  try {
    const response = await fetch(`${API_URL}/public/plans`);
    if (!response.ok) return FALLBACK_PLANS;
    const payload = (await response.json()) as { plans?: PublicPlan[] };
    if (payload.plans && payload.plans.length > 0) {
      const pyme = applyMarketingLabels(filterPymePlans(payload.plans));
      if (pyme.length > 0) return pyme;
    }
  } catch {
    /* keep fallback */
  }
  return FALLBACK_PLANS;
}

export type PlanId = "starter" | "growth" | "pro" | "firma_contable";

export type RncTier = {
  from: number;
  to: number | null;
  price: number;
};

export type RncTypeId = "basico" | "plus" | "empresarial";

export type RncType = {
  id: RncTypeId;
  label: string;
  ecf: number;
  users: number;
  price?: number;
  tiers?: RncTier[];
  equivalentPlanCode: "starter" | "growth" | "pro";
};

export type PoolPack = {
  ecf: number;
  price: number;
  unit: number;
};

export type PlanAddon = {
  id: string;
  label: string;
  price: number;
  type: "one_time" | "monthly";
  ecf?: number;
  users?: number;
};

export type FirmaPlan = {
  id: "firma_contable";
  name: string;
  audience: "accountants";
  badge: string;
  tagline: string;
  base_price: number;
  currency: string;
  included_rnc: number;
  included_users: number;
  included_ecf: number;
  rnc_types: RncType[];
  pool_packs: PoolPack[];
  pack_validity_months: number;
  pack_rollover: boolean;
  overage: { block: number; price: number };
  consumption_order: readonly ["monthly_quota", "pack_balance", "overage"];
  addons: PlanAddon[];
  annual_free_months: number;
  setup_fee: number;
  support_sla_hours: number;
};

export const FIRMA_CONTABLE: FirmaPlan = {
  id: "firma_contable",
  name: "Firma Contable",
  audience: "accountants",
  badge: "Para firmas contables",
  tagline: "Para el contador que gestiona la facturación electrónica de varios clientes.",
  base_price: 150,
  currency: "USD",
  included_rnc: 3,
  included_users: 4,
  included_ecf: 450,
  rnc_types: [
    {
      id: "basico",
      label: "RNC Básico",
      ecf: 150,
      users: 1,
      equivalentPlanCode: "starter",
      tiers: [
        { from: 4, to: 10, price: 42 },
        { from: 11, to: 25, price: 36 },
        { from: 26, to: null, price: 30 },
      ],
    },
    { id: "plus", label: "RNC Plus", ecf: 500, users: 2, price: 70, equivalentPlanCode: "growth" },
    { id: "empresarial", label: "RNC Empresarial", ecf: 2000, users: 4, price: 140, equivalentPlanCode: "pro" },
  ],
  pool_packs: [
    { ecf: 500, price: 50, unit: 0.1 },
    { ecf: 1500, price: 135, unit: 0.09 },
    { ecf: 5000, price: 375, unit: 0.075 },
  ],
  pack_validity_months: 12,
  pack_rollover: true,
  overage: { block: 50, price: 6 },
  consumption_order: ["monthly_quota", "pack_balance", "overage"],
  addons: [
    { id: "rnc_setup", label: "Nuevo cliente", price: 75, type: "one_time" },
    { id: "firm_user", label: "Usuario adicional de la firma", price: 10, type: "monthly" },
    { id: "spinoff", label: "Separación a base propia", price: 250, type: "one_time" },
  ],
  annual_free_months: 2,
  setup_fee: 0,
  support_sla_hours: 8,
};

export const FIRMA_FEATURES = [
  "Panel consolidado: el estado ante la DGII de todos tus clientes en una sola pantalla",
  "3 RNC de clientes incluidos (adicional desde $30/mes)",
  "4 usuarios de la firma con acceso a todas las empresas",
  "450 e-CF mensuales en pool compartido entre tus clientes",
  "Una sola base de datos Odoo, multi-empresa, con respaldo diario",
  "Cada cliente firma con su propio certificado P12 y su propio RNC",
  "Reportes 606, 607 y 608 automáticos por cliente, exportables en lote",
  "Alertas de rechazo DGII dirigidas a la firma, no a tu cliente",
  "Alta de cliente nuevo: USD$75 (vs. USD$350 del alta individual)",
  "Soporte 8h hábiles + office hours quincenales para tu equipo",
];

export const FIRMA_HOOK = "Hasta 50% menos que contratar un plan por cada cliente.";

export const FIRMA_FOOTNOTE =
  "Pago anual: 2 meses gratis. Cada cliente requiere su propio certificado digital y su propio proceso de certificación ante la DGII; te acompañamos en cada uno.";

export const FIRMA_WHATSAPP_URL = "https://wa.me/18098397458";
export const FIRMA_CTA_PRIMARY = "Hablemos";
export const FIRMA_CLOSING =
  "Cada cliente que agregas trae su propia capacidad. No hay letra chica: 150 comprobantes y un usuario por cliente, siempre.";

export const FIRMA_RNC_ROWS: { clients: string; price: string }[] = [
  { clients: "1 a 3", price: "Incluidos en la base" },
  { clients: "4 a 10", price: "USD$42" },
  { clients: "11 a 25", price: "USD$36" },
  { clients: "26 o más", price: "USD$30" },
];

export const FIRMA_ADDON_ROWS: { label: string; price: string }[] = [
  {
    label: "Nuevo cliente (setup + acompañamiento a certificación DGII)",
    price: "USD$75 único",
  },
  { label: "Usuario adicional de la firma", price: "USD$10/mes" },
  { label: "Separación de cliente a base propia (spin-off)", price: "USD$250 único" },
];

export function pymePrice(code: "starter" | "growth" | "pro") {
  return FALLBACK_PLANS.find((p) => p.code === code)?.price_monthly ?? 0;
}

export function pymeName(code: "starter" | "growth" | "pro") {
  return FALLBACK_PLANS.find((p) => p.code === code)?.name ?? code;
}
